import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { Bell, Check, CheckCheck, Trash2, X, Store, FileText, RefreshCw, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NotificationBell = () => {
  const { token, user, isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);
  const audioRef = useRef(null);
  const lastCountRef = useRef(0);
  const isFirstLoadRef = useRef(true);

  // Determine API path based on user role
  const getApiPath = useCallback(() => {
    return isSuperAdmin() ? '/admin/notifications' : '/store/notifications';
  }, [isSuperAdmin]);

  // Initialize audio
  useEffect(() => {
    audioRef.current = new Audio('/notification.mp3');
    audioRef.current.volume = 0.5;
  }, []);

  // Play notification sound
  const playNotificationSound = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(err => {
        console.log('Audio play failed:', err);
      });
    }
  }, []);

  // Fetch unread count
  const fetchUnreadCount = useCallback(async () => {
    if (!token || !user) return;
    
    try {
      const apiPath = isSuperAdmin() ? '/admin/notifications/unread-count' : '/store/notifications/unread-count';
      const response = await axios.get(`${API}${apiPath}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const newCount = response.data.count;
      
      // Play sound if count increased (and not first load)
      if (!isFirstLoadRef.current && newCount > lastCountRef.current) {
        playNotificationSound();
        const notifType = isSuperAdmin() ? 'New ticket notification!' : 'Ticket status updated!';
        toast.info(notifType, {
          description: isSuperAdmin() ? 'A new ticket has been created.' : 'A ticket status has been changed.',
          duration: 5000
        });
      }
      
      lastCountRef.current = newCount;
      isFirstLoadRef.current = false;
      setUnreadCount(newCount);
    } catch (error) {
      console.error('Failed to fetch unread count:', error);
    }
  }, [token, user, isSuperAdmin, playNotificationSound]);

  // Fetch notifications
  const fetchNotifications = useCallback(async () => {
    if (!token || !user) return;
    
    setLoading(true);
    try {
      const apiPath = getApiPath();
      const response = await axios.get(`${API}${apiPath}?limit=20`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(response.data);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setLoading(false);
    }
  }, [token, user, getApiPath]);

  // Poll for new notifications every 10 seconds
  useEffect(() => {
    if (!user) return;
    
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 10000);
    
    return () => clearInterval(interval);
  }, [fetchUnreadCount, user]);

  // Fetch full notifications when dropdown opens
  useEffect(() => {
    if (isOpen) {
      fetchNotifications();
    }
  }, [isOpen, fetchNotifications]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle notification click - navigate to ticket
  const handleNotificationClick = async (notification) => {
    // Mark as read if not already
    if (!notification.is_read) {
      await markAsRead(notification.id);
    }
    
    // Close the dropdown
    setIsOpen(false);
    
    // Navigate to ticket details if ticket_id exists
    if (notification.ticket_id) {
      navigate(`/tickets/${notification.ticket_id}`);
    }
  };

  // Mark as read
  const markAsRead = async (notificationId) => {
    try {
      const apiPath = getApiPath();
      await axios.post(`${API}${apiPath}/${notificationId}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      toast.error('Failed to mark as read');
    }
  };

  // Mark all as read
  const markAllAsRead = async () => {
    try {
      const apiPath = getApiPath();
      await axios.post(`${API}${apiPath}/mark-all-read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
      toast.success('All notifications marked as read');
    } catch (error) {
      toast.error('Failed to mark all as read');
    }
  };

  // Delete notification
  const deleteNotification = async (notificationId, e) => {
    e.stopPropagation();
    try {
      const apiPath = getApiPath();
      await axios.delete(`${API}${apiPath}/${notificationId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const notification = notifications.find(n => n.id === notificationId);
      if (notification && !notification.is_read) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
    } catch (error) {
      toast.error('Failed to delete notification');
    }
  };

  // Get notification type icon and color
  const getNotificationStyle = (type) => {
    if (type === 'status_update') {
      return { bgColor: 'bg-green-100', iconColor: 'text-green-600' };
    }
    return { bgColor: 'bg-indigo-100', iconColor: 'text-indigo-600' };
  };

  // Don't render if no user
  if (!user) return null;

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 rounded-lg hover:bg-slate-100 transition-colors"
        data-testid="notification-bell"
      >
        <Bell className={`w-6 h-6 ${unreadCount > 0 ? 'text-indigo-600' : 'text-slate-600'}`} />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center animate-pulse">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-96 bg-white rounded-xl shadow-xl border border-slate-200 z-50 overflow-hidden">
          {/* Header */}
          <div className="px-4 py-3 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
            <h3 className="font-semibold text-slate-900">
              {isSuperAdmin() ? 'Admin Notifications' : 'Store Notifications'}
            </h3>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={markAllAsRead}
                  className="text-xs text-indigo-600 hover:text-indigo-700"
                  data-testid="mark-all-read-btn"
                >
                  <CheckCheck className="w-4 h-4 mr-1" />
                  Mark all read
                </Button>
              )}
              <button
                onClick={() => setIsOpen(false)}
                className="text-slate-400 hover:text-slate-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Notifications List */}
          <div className="max-h-96 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
              </div>
            ) : notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-slate-400">
                <Bell className="w-12 h-12 mb-2" />
                <p className="text-sm">No notifications yet</p>
                <p className="text-xs mt-1 text-slate-300">
                  {isSuperAdmin() 
                    ? 'You\'ll be notified when tickets are created' 
                    : 'You\'ll be notified when ticket status changes'}
                </p>
              </div>
            ) : (
              notifications.map((notification) => {
                const style = getNotificationStyle(notification.type);
                const hasTicket = !!notification.ticket_id;
                return (
                  <div
                    key={notification.id}
                    className={`px-4 py-3 border-b border-slate-100 hover:bg-slate-50 cursor-pointer transition-all group ${
                      !notification.is_read ? 'bg-indigo-50/50' : ''
                    } ${hasTicket ? 'hover:bg-indigo-50' : ''}`}
                    onClick={() => handleNotificationClick(notification)}
                    data-testid={`notification-item-${notification.id}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`p-2 rounded-lg ${!notification.is_read ? style.bgColor : 'bg-slate-100'} group-hover:scale-105 transition-transform`}>
                        {notification.type === 'status_update' ? (
                          <RefreshCw className={`w-4 h-4 ${!notification.is_read ? style.iconColor : 'text-slate-500'}`} />
                        ) : (
                          <FileText className={`w-4 h-4 ${!notification.is_read ? style.iconColor : 'text-slate-500'}`} />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2">
                            <p className={`text-sm font-medium truncate ${!notification.is_read ? 'text-slate-900' : 'text-slate-600'}`}>
                              {notification.title}
                            </p>
                            {hasTicket && (
                              <ExternalLink className="w-3 h-3 text-indigo-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                            )}
                          </div>
                          <button
                            onClick={(e) => deleteNotification(notification.id, e)}
                            className="text-slate-400 hover:text-red-500 flex-shrink-0"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                        <p className="text-xs text-slate-500 mt-1 truncate">{notification.message}</p>
                        <div className="flex items-center justify-between mt-2">
                          <div className="flex items-center gap-2">
                            <Badge className="bg-slate-100 text-slate-600 text-xs flex items-center gap-1">
                              <Store className="w-3 h-3" />
                              {notification.store_name}
                            </Badge>
                            {notification.ticket_number && (
                              <Badge className="bg-indigo-50 text-indigo-600 text-xs">
                                {notification.ticket_number}
                              </Badge>
                            )}
                            <span className="text-xs text-slate-400">
                              {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
                            </span>
                          </div>
                          {hasTicket && (
                            <span className="text-xs text-indigo-500 font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                              View Ticket →
                            </span>
                          )}
                        </div>
                      </div>
                      {!notification.is_read && (
                        <div className="w-2 h-2 bg-indigo-500 rounded-full flex-shrink-0 mt-2"></div>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="px-4 py-2 border-t border-slate-200 bg-slate-50 text-center">
              <span className="text-xs text-slate-500">
                Showing {notifications.length} most recent notifications • Click to view ticket
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
